'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Sidebar from "@/components/Sidebar";
import { LiquidAmbientBackground, MaxTopSpatialOmnibar } from "@/components/ui/LiquidGlass";
import { Menu, ShieldCheck, ChevronRight, PanelLeftClose, PanelLeft } from 'lucide-react';
import MfaCountdownBanner from '@/components/security/MfaCountdownBanner';
import MaxFloatingCopilot from '@/components/wallet/MaxFloatingCopilot';
import { useCorporate } from '@/context/CorporateContext';

function getPageTitle(pathname: string): string {
    if (pathname.startsWith('/master/treasury')) return 'Torre de Control';
    if (pathname.startsWith('/master/agents')) return 'Asistentes & Auto-Earn';
    if (pathname.startsWith('/master/settlements')) return 'Liquidaciones & Clearing';
    if (pathname.startsWith('/master/credit')) return 'Créditos & Términos Legal';
    if (pathname.startsWith('/master/legajo')) return 'Legajo de Usuarios';
    if (pathname.startsWith('/master/compliance')) return 'Cumplimiento CNV/UIF';
    if (pathname.startsWith('/master/merchants')) return 'Comercios & Partners';
    if (pathname.startsWith('/master/corporates')) return 'Corporates & Tenants';
    if (pathname.startsWith('/master/transactions')) return 'Transaccional & Libros';
    if (pathname.startsWith('/master/audit')) return 'Bitácora de Auditoría';
    if (pathname.startsWith('/master/fees')) return 'Esquemas de Comisiones';
    if (pathname.startsWith('/master/limits')) return 'Límites Transaccionales';
    if (pathname.startsWith('/master/webhooks')) return 'Webhooks & Eventos';
    if (pathname.startsWith('/master/api-keys')) return 'Credenciales API';
    if (pathname.startsWith('/master/users')) return 'Gestión de Operadores';
    if (pathname.startsWith('/master/support')) return 'Tickets & Soporte';
    if (pathname.startsWith('/master/metrics')) return 'Métricas & BI';
    return 'Consola Master';
}

export default function AdminLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const pathname = usePathname();
    const { config: corporateConfig } = useCorporate();

    // Cargar preferencia persistida de colapso de barra lateral en escritorio
    useEffect(() => {
        try {
            const saved = localStorage.getItem('master_sidebar_collapsed');
            if (saved === 'true') {
                setIsSidebarCollapsed(true);
            }
        } catch (_) {}
    }, []);

    const toggleSidebarCollapse = () => {
        setIsSidebarCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('master_sidebar_collapsed', String(next));
            } catch (_) {}
            return next;
        });
    };

    useEffect(() => {
        // Enforce pure light theme across the entire Master suite
        if (typeof document !== 'undefined') {
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
            try {
                localStorage.setItem('theme', 'light');
            } catch (_) {}
        }
    }, []);

    // Bloquear scroll de fondo cuando el menú móvil esté abierto
    useEffect(() => {
        if (typeof document !== 'undefined') {
            if (isMobileMenuOpen) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
        return () => {
            if (typeof document !== 'undefined') {
                document.body.style.overflow = '';
            }
        };
    }, [isMobileMenuOpen]);

    // Cerrar menú móvil al navegar
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    return (
        <div className="master-suite-shell flex min-h-screen bg-[#f8fafc] text-slate-900 transition-colors duration-300 relative overflow-x-hidden font-sans">
            <LiquidAmbientBackground fixed={true} intensity="vibrant" />

            {/* Backdrop oscuro con blur para pantallas móviles cuando el drawer está activo */}
            {isMobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-40 md:hidden transition-opacity duration-300 animate-in fade-in"
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-label="Cerrar menú móvil"
                    role="button"
                    tabIndex={0}
                />
            )}

            {/* Sidebar drawer móvil / fijo en escritorio */}
            <Sidebar 
                isOpenMobile={isMobileMenuOpen}
                onCloseMobile={() => setIsMobileMenuOpen(false)}
                isCollapsedDesktop={isSidebarCollapsed}
                onToggleCollapseDesktop={toggleSidebarCollapse}
            />

            {/* Contenedor principal responsive: margen dinámico según estado de colapso */}
            <div className={`flex-1 ${isSidebarCollapsed ? 'ml-0' : 'ml-0 md:ml-68'} transition-all duration-300 p-0 min-w-0 relative z-10 w-full flex flex-col`}>
                {/* Barra Superior Ultra Liquid Glass (Móvil + Escritorio) con Max Omnibar Central */}
                {!pathname.startsWith('/master/login') && (
                    <header className="sticky top-0 z-30 flex flex-wrap lg:flex-nowrap items-center justify-between gap-3 px-4 sm:px-8 py-3 glass-card-obsidian border-x-0 border-t-0">
                        <div className="flex items-center gap-3 min-w-0 shrink-0">
                            <button
                                type="button"
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="p-2.5 rounded-2xl glass-pill-obsidian text-slate-700 hover:text-slate-950 transition active:scale-95 cursor-pointer shrink-0 md:hidden"
                                aria-label="Abrir menú de navegación"
                                title="Abrir menú"
                            >
                                <Menu className="size-5 text-slate-800" />
                            </button>

                            <button
                                type="button"
                                onClick={toggleSidebarCollapse}
                                className="hidden md:flex p-2.5 rounded-2xl glass-pill-obsidian text-slate-700 hover:text-blue-600 transition active:scale-95 cursor-pointer shrink-0"
                                title={isSidebarCollapsed ? 'Mostrar barra lateral' : 'Ocultar barra lateral'}
                            >
                                {isSidebarCollapsed ? (
                                    <PanelLeft className="size-5 text-blue-600" />
                                ) : (
                                    <PanelLeftClose className="size-5 text-blue-600" />
                                )}
                            </button>

                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <span className="size-1.5 rounded-full bg-blue-600 animate-pulse shrink-0" />
                                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-600 truncate">
                                        {corporateConfig?.name ? `${corporateConfig.name} • Backoffice` : 'Backoffice Central'}
                                    </p>
                                </div>
                                <h2 className="text-xs sm:text-sm font-black text-slate-900 tracking-tight truncate leading-tight mt-0.5">
                                    {getPageTitle(pathname)}
                                </h2>
                            </div>
                        </div>

                        {/* Barra Superior Central de Max (Omnibar Espacial) */}
                        <div className="order-3 lg:order-2 w-full lg:w-auto lg:flex-1 px-0 lg:px-4">
                            <MaxTopSpatialOmnibar portalMode="master" />
                        </div>

                        <div className="order-2 lg:order-3 flex items-center gap-2 px-3.5 py-1.5 glass-pill-obsidian rounded-full shrink-0">
                            <ShieldCheck className="size-3.5 text-emerald-600" />
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">
                                Sistemas Activos
                            </span>
                        </div>
                    </header>
                )}

                {/* Banner de Gracia de Seguridad 7 Días & Suspensiones CNV */}
                {!pathname.startsWith('/master/login') && <MfaCountdownBanner />}

                {/* Contenido de la página */}
                <main className="flex-1 w-full min-w-0">
                    {children}
                </main>
                {!pathname.startsWith('/master/login') && <MaxFloatingCopilot />}
            </div>
        </div>
    );
}
